// 미응답 경고 — 기준 시간 읽기 · 대상 고르기 · 알림 재료(cron 이 저장소 밖에서 한 벌로 판정한다).

import { isIntakeCustomer, minutesBetween } from "./first-contact";
import type { Customer } from "./types";
import type { NotifyPayloadMap } from "../notify/adapters";
import { ORG_SETTING_DEFAULT } from "../org/types";

export const UNANSWERED_HOURS_OPTIONS = ["off", "1", "3", "24"] as const;
export type UnansweredHoursOption = (typeof UNANSWERED_HOURS_OPTIONS)[number];

export const UNANSWERED_HOURS_LABEL: Record<UnansweredHoursOption, string> = {
  off: "끔",
  "1": "1시간",
  "3": "3시간",
  "24": "24시간",
};

// 첫 실행 때 옛 문의가 한꺼번에 울리지 않게 최근 7일 접수만 본다.
export const UNANSWERED_WINDOW_DAYS = 7;

const DAY_MS = 86_400_000;

export const isUnansweredHoursOption = (value: unknown): value is UnansweredHoursOption =>
  typeof value === "string" && (UNANSWERED_HOURS_OPTIONS as readonly string[]).includes(value);

// 저장된 값 → 선택지. 비었거나 모르는 값은 기본(3시간).
export const unansweredOptionOf = (value: string | null | undefined): UnansweredHoursOption => {
  const fallback = ORG_SETTING_DEFAULT["intake.unanswered_hours"] as UnansweredHoursOption;
  return isUnansweredHoursOption(value) ? value : fallback;
};

// 기준 시간(시). 끄면 null.
export const unansweredHoursOf = (value: string | null | undefined): number | null => {
  const option = unansweredOptionOf(value);
  return option === "off" ? null : Number(option);
};

// 저장소가 읽어 올 후보 범위의 시작 — 이 시각 이후 접수.
export const unansweredSince = (now: Date): string =>
  new Date(now.getTime() - UNANSWERED_WINDOW_DAYS * DAY_MS).toISOString();

// 저장소가 모아 오는 후보 한 줄 — 고객 칸 일부 + 담당 이름 + 그 조직의 기준 시간 설정 원문 + 견적(초안 포함) 유무.
export type UnansweredIntakeRow = Pick<
  Customer,
  "id" | "org_id" | "name" | "stage" | "created_at" | "first_contacted_at" | "meta"
> & { assignee_name: string | null; hours_setting: string | null; has_quote: boolean };

export type UnansweredTarget = UnansweredIntakeRow & { minutes: number };

export const selectUnanswered = (rows: readonly UnansweredIntakeRow[], now: Date): UnansweredTarget[] => {
  const nowIso = now.toISOString();
  const since = Date.parse(unansweredSince(now));
  const out: UnansweredTarget[] = [];
  for (const row of rows) {
    if (row.stage !== "NEW" || row.first_contacted_at || row.has_quote || !isIntakeCustomer(row)) continue;
    // DB 는 `+00:00`, 앱은 `Z` 로 적어 문자열로 견주지 않는다.
    if (!(Date.parse(row.created_at) >= since)) continue;
    const threshold = unansweredHoursOf(row.hours_setting);
    const minutes = minutesBetween(row.created_at, nowIso);
    if (threshold === null || minutes === null || minutes < threshold * 60) continue;
    out.push({ ...row, minutes });
  }
  return out.sort((a, b) => a.id.localeCompare(b.id));
};

export const customerDetailPath = (customerId: string): string => `/app/customers/${customerId}`;

// 기준 주소가 있을 때만 절대 링크.
export const customerDetailLink = (origin: string | null, customerId: string): string | undefined =>
  origin ? `${origin.replace(/\/+$/, "")}${customerDetailPath(customerId)}` : undefined;

export const unansweredPayload = (
  target: UnansweredTarget,
  origin: string | null
): NotifyPayloadMap["intake.unanswered"] => ({
  customer_name: target.name,
  minutes: target.minutes,
  assignee: target.assignee_name?.trim() || undefined,
  link: customerDetailLink(origin, target.id),
});
